import React, { useEffect, useState, useMemo } from "react";
import { 
  Container, Row, Col, Card, CardBody, Input, 
  Button, Badge, Spinner, InputGroup 
} from "reactstrap";
import { 
  Copy, Search, Mail, User, Hash, RefreshCcw, ExternalLink 
} from "lucide-react";
import { toast, ToastContainer } from 'react-toastify';
import moment from "moment";
import { fetchComAuth } from "../../common/api";
import { api } from "../../config";

const SenhasRenovacao = () => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");

  const carregarSenhas = async () => {
    setLoading(true);
    try {
      const response = await fetchComAuth(`${api.API_URL}/lista-senhas`, { method: 'GET' });
      const result = await response.json();
      
      if (result.status) {
        setDados(result.data);
      }
    } catch (error) {
      toast.error("Não foi possível carregar as senhas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarSenhas();
    // Refresh automático a cada 5 minutos
    const interval = setInterval(carregarSenhas, 300000);
    return () => clearInterval(interval);
  }, []);

  const dadosFiltrados = useMemo(() => {
    return dados.filter(item => 
      item.nome_expositor?.toLowerCase().includes(busca.toLowerCase()) ||
      item.nome_estande?.toString().includes(busca) ||
      item.email_renovacao?.toLowerCase().includes(busca.toLowerCase())
    );
  }, [busca, dados]);

  const copiarParaClipboard = (texto) => {
    navigator.clipboard.writeText(texto);
    toast.success("Senha copiada com sucesso!", { position: "bottom-right", autoClose: 2000 });
  };

  return (
    <div className="page-content">
      <ToastContainer />
      <Container fluid>
        {/* Cabeçalho de Controle */}
        <Row className="mb-4 align-items-center">
          <Col md={6}>
            <h4 className="mb-0">Controle de Senhas de Renovação</h4>
            <p className="text-muted mb-0">Senhas geradas no dia de hoje ({moment().format('DD/MM/YYYY')})</p>
          </Col>
          <Col md={6} className="text-end">
            <div className="d-flex gap-2 justify-content-md-end mt-3 mt-md-0">
              <div className="position-relative" style={{ minWidth: '250px' }}>
                <InputGroup>
                    <Input 
                        placeholder="Expositor ou Estande..." 
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                    />
                    <Button color="light"><Search size={16} /></Button>
                </InputGroup>
              </div>
              <Button color="soft-primary" onClick={carregarSenhas} disabled={loading}>
                <RefreshCcw size={16} className={loading ? "spin" : ""} />
              </Button>
            </div>
          </Col>
        </Row>

        {loading && dados.length === 0 ? (
          <div className="text-center p-5">
            <Spinner color="primary" />
            <p className="mt-2">Sincronizando senhas...</p>
          </div>
        ) : (
          <Row>
            {dadosFiltrados.length > 0 ? (
              dadosFiltrados.map((item, index) => (
                <Col xl={4} lg={6} key={`${item.expositor_id}-${item.estande_id}`}>
                  <Card className="shadow-sm border-0 mb-4 overflow-hidden">
                    <div className="bg-primary-subtle p-2">
                        <div className="d-flex justify-content-between align-items-center px-2">
                            <Badge color="primary">{item.feira_nome}</Badge>
                            <small className="text-primary fw-medium">
                                <Hash size={12} /> Estande: {item.nome_estande}
                            </small>
                        </div>
                    </div>
                    <CardBody>
                      <div className="mb-3">
                        <h5 className="fs-15 mb-1 text-truncate">{item.nome_expositor}</h5>
                        <div className="d-flex align-items-center text-muted fs-12">
                          <Mail size={12} className="me-1" /> {item.email_renovacao}
                        </div>
                      </div>

                      <div className="p-3 rounded bg-light border d-flex align-items-center justify-content-between">
                        <div>
                          <small className="text-uppercase text-muted fw-bold d-block" style={{ fontSize: '10px' }}>Senha de Acesso</small>
                          <span className="fs-22 fw-bold text-dark font-monospace tracking-wider">
                            {item.senha_gerada}
                          </span>
                        </div>
                        <Button 
                          color="primary" 
                          className="btn-icon"
                          onClick={() => copiarParaClipboard(item.senha_gerada)}
                        >
                          <Copy size={18} />
                        </Button>
                      </div>

                      <div className="mt-3 pt-3 border-top d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center text-muted fs-13">
                            <User size={14} className="me-1" /> 
                            <span>Comercial: <b>{item.nome_comercial}</b></span>
                        </div>
                        <small className="text-muted">
                            Gerada às {moment(item.updated_at).format('HH:mm')}
                        </small>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))
            ) : (
              <Col lg={12}>
                <div className="text-center p-5 border rounded bg-white shadow-sm">
                  <div className="avatar-lg mx-auto mb-3">
                    <div className="avatar-title bg-light text-primary rounded-circle fs-24">
                       <Search size={40} />
                    </div>
                  </div>
                  <h5>Nenhuma senha encontrada</h5>
                  <p className="text-muted">Não há registros de alterações para os seus clientes hoje ou o filtro de busca não retornou resultados.</p>
                </div>
              </Col>
            )}
          </Row>
        )}
      </Container>
      <style>{`
        .font-monospace { font-family: 'JetBrains Mono', 'Courier New', monospace !important; }
        .tracking-wider { letter-spacing: 0.1em; }
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default SenhasRenovacao;